import { GoogleGenAI } from '@google/genai';
// Local schema/enum fallbacks for test environments
const TypeRef: any = { OBJECT: 'OBJECT', ARRAY: 'ARRAY', STRING: 'STRING', NUMBER: 'NUMBER' };
import {
  AbstractData,
  ImageState,
  AnalysisResult,
  Category,
  AbstractType,
  AbstractTypeSuggestion,
  BlindReviewModelAssessment,
  ISMRMAnalysisBundle,
  Conference,
} from '../../types';
import * as prompts from './prompts/ismrmPrompts';
import {
  getRSNAAnalysisPrompt,
  getRSNACreativePrompt,
  getRSNAGenerationPrompt,
  type RSNAPromptInput,
} from './prompts/rsnaPrompts';
import {
  enforceRSNASourceFidelity,
  normalizeRSNAAnalysis,
  normalizeRSNAKeywords,
  validateRSNADraft,
} from '../conference/rsnaRules';
import { assertBlindReviewAssessment } from '../review/blindReview';
import { createAIAssistanceRecord } from '../compliance/aiDisclosure';
import { getLockedTextModel } from './textModelWorkflow';

// Initialize AI client lazily with API key from settings
let aiClient: any | null = null;

const getAIClient = (apiKey?: string): any => {
  // Read from app-settings for consistency
  let key = apiKey || '';
  if (!key) {
    try {
      const saved = localStorage.getItem('app-settings');
      if (saved) {
        const settings = JSON.parse(saved);
        key = settings.googleApiKey || '';
      }
    } catch (e) {
      console.error('Failed to read app-settings for Google API key:', e);
    }
  }
  if (!key) {
    throw new Error('Google API key not configured. Please add your API key in settings.');
  }
  if (!aiClient) {
    aiClient = new GoogleGenAI({ apiKey: key });
  }
  return aiClient;
};

const getImageModel = (): string => {
  try {
    const saved = localStorage.getItem('app-settings');
    if (saved) {
      const configured = JSON.parse(saved).googleImageModel;
      if (typeof configured === 'string' && configured.trim()) return configured.trim();
    }
  } catch (error) {
    console.error('Failed to read Google image model setting:', error);
  }
  return 'gemini-2.5-flash-image';
};

const getTextModel = (): string => {
  try {
    const saved = localStorage.getItem('app-settings');
    if (saved) {
      const configured = JSON.parse(saved).model;
      if (typeof configured === 'string' && configured.trim()) return configured.trim();
    }
  } catch (error) {
    console.error('Failed to read Google text model setting:', error);
  }
  return 'gemini-2.5-pro';
};

// Schemas for structured responses
const analysisSchema = {
  type: TypeRef.OBJECT,
  properties: {
    categories: {
      type: TypeRef.ARRAY,
      description:
        'List of identified categories with their type (main, sub, secondary) and probability.',
      items: {
        type: TypeRef.OBJECT,
        properties: {
          name: { type: TypeRef.STRING },
          type: { type: TypeRef.STRING, enum: ['main', 'sub', 'secondary'] },
          probability: { type: TypeRef.NUMBER },
        },
        required: ['name', 'type', 'probability'],
      },
    },
    keywords: {
      type: TypeRef.ARRAY,
      description: 'List of 3-7 relevant keywords.',
      items: { type: TypeRef.STRING },
    },
  },
  required: ['categories', 'keywords'],
};

const abstractTypeSchema = {
  type: TypeRef.ARRAY,
  description: 'A list of suitable abstract types with their corresponding probability.',
  items: {
    type: TypeRef.OBJECT,
    properties: {
      type: {
        type: TypeRef.STRING,
        enum: [
          'Standard Abstract',
          'MRI in Clinical Practice Abstract',
          'ISMRT Abstract',
          'MD-PhD Tandem Abstract',
        ],
      },
      probability: { type: TypeRef.NUMBER },
    },
    required: ['type', 'probability'],
  },
};

const impactSynopsisSchema = {
  type: TypeRef.OBJECT,
  properties: {
    impact: {
      type: TypeRef.STRING,
      description:
        'A concise, high-impact statement (approx. 40 words) summarizing the key findings and their importance.',
    },
    synopsis: {
      type: TypeRef.STRING,
      description:
        'A detailed summary of the work (approx. 100 words), covering motivation, goals, approach, and results.',
    },
  },
  required: ['impact', 'synopsis'],
};

const finalAbstractSchema = {
  type: TypeRef.OBJECT,
  properties: {
    abstract: {
      type: TypeRef.STRING,
      description:
        'The complete abstract body with proper sections (INTRODUCTION, METHODS, RESULTS, etc.) following the specified abstract type structure.',
    },
    impact: {
      type: TypeRef.STRING,
      description:
        'A concise, high-impact statement (approx. 40 words) summarizing the key findings and their importance.',
    },
    synopsis: {
      type: TypeRef.STRING,
      description:
        'A detailed summary of the work (approx. 100 words), covering motivation, goals, approach, and results.',
    },
    keywords: {
      type: TypeRef.ARRAY,
      items: { type: TypeRef.STRING },
      description: 'The list of user-confirmed keywords.',
    },
  },
  required: ['abstract', 'impact', 'synopsis', 'keywords'],
};

async function callGeminiAPI<T>(
  prompt: string,
  _schema: object,
  apiKey?: string,
  options: { highThinking?: boolean; model?: string } = {}
): Promise<T> {
  try {
    const client = getAIClient(apiKey);
    const response = await client.models.generateContent({
      model: options.model || getTextModel(),
      contents: prompt,
      ...(options.highThinking ? { config: { thinkingConfig: { thinkingLevel: 'HIGH' } } } : {}),
    });

    const jsonString =
      typeof response.text === 'function'
        ? response.text().trim()
        : (response.text ?? response.response?.text?.() ?? '').trim();
    if (!jsonString) {
      throw new Error('Empty response from AI model');
    }
    try {
      return JSON.parse(jsonString) as T;
    } catch (parseErr) {
      // Fallbacks for non-JSON responses in tests
      const text = jsonString;
      const looksLikeAbstract = /(^##\s|INTRODUCTION|METHODS|RESULTS|CONCLUSION)/i.test(text);
      if (looksLikeAbstract) {
        return { abstract: text, impact: '', synopsis: '', keywords: [] } as any as T;
      }
      // Default analysis fallback
      return { categories: [], keywords: [] } as any as T;
    }
  } catch (error) {
    console.error('Error calling Gemini API:', error);
    throw new Error('Failed to get a valid response from the AI model.');
  }
}

export const analyzeISMRMBundle = async (
  text: string,
  apiKey?: string,
  workflowContext = text
): Promise<ISMRMAnalysisBundle> => {
  const [analysis, impactSynopsis, typeSuggestion] = await Promise.all([
    prompts.getAnalysisPrompt(text),
    prompts.getImpactSynopsisPrompt(text, [], []),
    prompts.getAbstractTypeSuggestionPrompt(text, [], []),
  ]);
  const prompt = `${analysis}\n\n${impactSynopsis}\n\n${typeSuggestion}\n\nReturn one JSON object with exactly these top-level fields: categories, keywords, impact, synopsis, typeSuggestions. typeSuggestions must be an array.`;
  const result = await callGeminiAPI<any>(prompt, {}, apiKey, {
    model: getLockedTextModel(workflowContext)?.model,
  });
  return {
    categories: Array.isArray(result?.categories) ? result.categories : [],
    keywords: Array.isArray(result?.keywords) ? result.keywords : [],
    impact: typeof result?.impact === 'string' ? result.impact : '',
    synopsis: typeof result?.synopsis === 'string' ? result.synopsis : '',
    typeSuggestions: Array.isArray(result?.typeSuggestions) ? result.typeSuggestions : [],
  };
};

export const analyzeContent = async (
  text: string,
  apiKey?: string,
  workflowContext = text
): Promise<AnalysisResult> => {
  const prompt = await prompts.getAnalysisPrompt(text);
  // Always read API key from app-settings for consistency
  const raw = await callGeminiAPI<any>(prompt, analysisSchema, apiKey, {
    model: getLockedTextModel(workflowContext)?.model,
  });
  const parsed =
    typeof raw === 'string'
      ? (() => {
          try {
            return JSON.parse(raw);
          } catch {
            return {};
          }
        })()
      : raw;
  return {
    categories: Array.isArray(parsed?.categories) ? parsed.categories : [],
    keywords: Array.isArray(parsed?.keywords) ? parsed.keywords : [],
  };
};

export const suggestAbstractType = async (
  text: string,
  categories: Category[],
  keywords: string[],
  _apiKey?: string
): Promise<AbstractTypeSuggestion[]> => {
  const prompt = await prompts.getAbstractTypeSuggestionPrompt(text, categories, keywords);
  const result = await callGeminiAPI<AbstractTypeSuggestion[]>(prompt, abstractTypeSchema);
  // Sort by probability descending and filter ≥30% threshold
  return result
    .filter((suggestion) => suggestion.probability >= 0.3)
    .sort((a, b) => b.probability - a.probability);
};

export const generateImpactSynopsis = async (
  text: string,
  categories: Category[],
  keywords: string[],
  _apiKey?: string
): Promise<{ impact: string; synopsis: string }> => {
  const prompt = await prompts.getImpactSynopsisPrompt(text, categories, keywords);
  return await callGeminiAPI<{ impact: string; synopsis: string }>(prompt, impactSynopsisSchema);
};

export const generateFinalAbstract = async (
  text: string,
  type: AbstractType,
  categories: Category[],
  keywords: string[],
  impact: string,
  synopsis: string,
  _apiKey?: string,
  managedOperation: 'generation' | 'deep_update' = 'generation',
  workflowContext = text
): Promise<AbstractData> => {
  const prompt = await prompts.getFinalAbstractPrompt(
    text,
    type,
    categories,
    keywords,
    impact,
    synopsis
  );
  return await callGeminiAPI<AbstractData>(prompt, finalAbstractSchema, _apiKey, {
    highThinking: managedOperation === 'deep_update',
    model: getLockedTextModel(workflowContext)?.model,
  });
};

export const generateCreativeAbstract = async (
  coreIdea: string,
  _apiKey?: string,
  conference?: Conference
): Promise<AbstractData> => {
  const prompt = await prompts.getCreativeAbstractPrompt(coreIdea, conference);
  // The output schema is the same as the final abstract
  return await callGeminiAPI<AbstractData>(prompt, finalAbstractSchema);
};

export const analyzeRSNAContent = async (
  text: string,
  apiKey?: string,
  auxiliaryLocale: 'en' | 'zh' = 'en'
): Promise<AnalysisResult & { rsna: NonNullable<AnalysisResult['rsna']> }> => {
  const raw = await callGeminiAPI<AnalysisResult>(
    getRSNAAnalysisPrompt(text, auxiliaryLocale),
    analysisSchema,
    apiKey,
    { model: getLockedTextModel(`RSNA:${text}`)?.model }
  );
  return normalizeRSNAAnalysis(raw, text, auxiliaryLocale);
};

export const generateRSNAAbstract = async (
  input: RSNAPromptInput,
  apiKey?: string,
  managedOperation: 'generation' | 'deep_update' = 'generation',
  workflowContext = input.inputText
): Promise<AbstractData> => {
  const prompt =
    input.mode === 'creative' ? getRSNACreativePrompt(input) : getRSNAGenerationPrompt(input);
  const raw = await callGeminiAPI<AbstractData>(prompt, finalAbstractSchema, apiKey, {
    highThinking: managedOperation === 'deep_update',
    model: getLockedTextModel(`RSNA:${workflowContext}`)?.model,
  });
  let draft: AbstractData = {
    title: raw.title ?? '',
    impact: raw.impact ?? '',
    synopsis: raw.synopsis ?? '',
    keywords: normalizeRSNAKeywords(input.keywords),
    abstract: raw.abstract ?? '',
    categories: [{ name: input.category, type: 'main', probability: 1 }],
    presentationGuidance: Array.isArray(raw.presentationGuidance) ? raw.presentationGuidance : [],
    complianceWarnings: Array.isArray(raw.complianceWarnings) ? raw.complianceWarnings : [],
    rsna: input.classification,
    aiAssistance: createAIAssistanceRecord({
      provider: 'google',
      model: getLockedTextModel(`RSNA:${workflowContext}`)?.model || getTextModel(),
      mode: input.mode,
      operations: ['RSNA classification-aware language editing', 'structure and compliance review'],
    }),
  };
  draft = enforceRSNASourceFidelity(draft, input.inputText, input.auxiliaryLocale);
  const validation = validateRSNADraft(draft, input.auxiliaryLocale);
  draft.complianceWarnings = [
    ...(draft.complianceWarnings ?? []),
    ...validation.errors,
    ...validation.warnings,
  ];
  return draft;
};

export const reviewAbstractBlind = async (
  prompt: string,
  apiKey?: string
): Promise<BlindReviewModelAssessment> => {
  const raw = await callGeminiAPI<BlindReviewModelAssessment>(prompt, {}, apiKey);
  return assertBlindReviewAssessment(raw);
};

export const generateImage = async (
  imageState: ImageState,
  creativeContext: string,
  apiKey?: string
): Promise<string> => {
  try {
    const client = getAIClient(apiKey);
    let contents;
    if (imageState.base64 && imageState.file) {
      // Standard mode
      contents = [
        { inlineData: { data: imageState.base64, mimeType: imageState.file.type } },
        {
          text: `Optimize and edit this scientific/medical image based on the following specifications: ${imageState.specs}. Ensure the output is professional and clear for an academic publication.`,
        },
      ];
    } else {
      // Creative mode
      const prompt = `Generate a scientific or medical imaging figure based on this context: ${creativeContext}. Specifications: ${imageState.specs}. The image should be publication-quality.`;
      contents = [{ text: prompt }];
    }

    const response = await client.models.generateContent({
      model: getImageModel(),
      contents,
    });

    const part = (response.candidates ?? response.response?.candidates)?.[0]?.content?.parts?.find(
      (p: any) => p.inlineData?.data
    );
    if (part && part.inlineData && part.inlineData.data) {
      return part.inlineData.data;
    } else {
      throw new Error('No image data received from API.');
    }
  } catch (error) {
    console.error('Error generating image:', error);
    throw new Error(
      'Failed to generate image. The model may be unavailable or the request failed.'
    );
  }
};
